import { useEffect, useRef, useState } from "react";

// Version au moment du chargement de l'app (injectée au build via REACT_APP_BUILD_ID).
// Fallback "dev" en local où la variable n'est pas définie.
// (lue au montage plutôt qu'au chargement du module : même valeur en production, et testable)
const loadedBuildId = () => process.env.REACT_APP_BUILD_ID || "dev";

const CHECK_INTERVAL_MS = 60_000; // poll toutes les 60s

/**
 * Détecte qu'un nouveau build a été déployé pendant que l'onglet est ouvert.
 * version.json est servi en no-cache (cf firebase.json) → reflète toujours le
 * dernier déploiement. Si le buildId distant diffère de celui chargé au démarrage,
 * on affiche un avis « Recharger » (VersionNotice) (jamais de reload forcé : l'utilisateur peut être
 * en pleine saisie / génération).
 */
// Rend true quand l'onglet tourne sur un ancien build : App affiche alors un avis qui RESTE jusqu’au clic (Alex
// 2026-10-29 : « Has to be permanent… if we use the normal notification, it'll fade and they won't see it »).
export default function useVersionCheck() {
  // En local (dev) il n'y a pas de version.json déployé → on n'active rien.
  const notifiedRef = useRef(false);
  const [stale, setStale] = useState(false);

  useEffect(() => {
    const LOADED_BUILD_ID = loadedBuildId();
    if (LOADED_BUILD_ID === "dev") return;

    let cancelled = false;

    const checkVersion = async () => {
      if (cancelled || notifiedRef.current || document.hidden) return;
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json();
        const remoteBuildId = data?.buildId;
        if (remoteBuildId && remoteBuildId !== LOADED_BUILD_ID) {
          notifiedRef.current = true;
          setStale(true);   // avis permanent (VersionNotice), plus un toast qui pouvait disparaître
        }
      } catch {
        // Réseau indisponible / fetch échoué → on retentera au prochain tick.
      }
    };

    const interval = setInterval(checkVersion, CHECK_INTERVAL_MS);
    // Re-check quand l'utilisateur revient sur l'onglet (cas typique : il revient
    // après un moment, un déploiement a eu lieu entre-temps).
    const onVisible = () => { if (!document.hidden) checkVersion(); };
    document.addEventListener("visibilitychange", onVisible);

    // Premier check après un court délai (laisse l'app finir de monter).
    const initial = setTimeout(checkVersion, 5_000);

    return () => {
      cancelled = true;
      clearInterval(interval);
      clearTimeout(initial);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  return stale;
}
